import type { HandView } from '@tctp/protocol';
import type { Highlight } from '../bestFive';
import type { Verification } from '../useVerification';
import { PlayingCard } from './PlayingCard';

/** The one fairness message that stays on the felt: a revealed card that does not match its commitment. */
function MismatchAlert({ verification }: { verification: Verification }) {
  const failure = verification.status === 'mismatch' ? verification.checks.find(c => !c.ok) : undefined;
  if (!failure) return null;
  return <div role="alert" className="fair-alert">A revealed card does not match the commitment at position {failure.position}. Stop playing and check the details.</div>;
}

/** The felt: pot pill (collected chips) with the total tag and, with more than one pot, the breakdown under it; board (each card dealt after `boardDelays[i]`), branding; the deal's verification lives in the options menu. */
export function Felt({ hand, collected, verification, boardDelays = [], board2Delays = [], behind = false, highlight = null, highlight2 = null, ritRemainingMs = null }: {
  hand: HandView | null; collected: number; verification: Verification; boardDelays?: number[]; highlight?: Highlight | null;
  /** Run it twice: deal delays for run 2's own cards, whether run 2 has started dealing (run 1 then fades behind it), the glow for run 2's board, and the vote's remaining time while it is open. */
  board2Delays?: number[]; behind?: boolean; highlight2?: Highlight | null; ritRemainingMs?: number | null;
}) {
  const twice = hand?.board2 ?? null;
  const live = hand !== null && hand.phase !== 'complete';
  const total = live ? hand.potTotal : 0;
  const shown = live ? collected : 0;
  const pots = live ? hand.pots : [];
  return (
    <div className="felt-rim">
      <div className="felt">
        <div className="pot-pill" data-testid="pot">
          {shown}
          {live && total !== shown && <span className="pot-total">total <b>{total}</b></span>}
          {pots.length > 1 && <span className="pot-breakdown">{pots.flatMap((p, i) => [i > 0 ? ' · ' : '', i === 0 ? 'Main ' : pots.length === 2 ? 'Side ' : `Side ${i} `, <b key={i}>{p.amount}</b>])}</span>}
        </div>
        <MismatchAlert verification={verification} />
        {hand && (hand.board.length > 0 || (hand.upcoming?.length ?? 0) > 0 || (hand.rabbit?.length ?? 0) > 0) && (
          <div className={`board${twice && behind ? ' behind' : ''}`}>
            {hand.board.map((c, i) => <PlayingCard key={c} card={c} size="lg" dealDelayMs={boardDelays[i] ?? 0} flip used={highlight?.used.has(c)} unused={highlight !== null && !highlight.used.has(c)} tilt={highlight?.tilt.get(c) ?? 0} />)}
            {hand.upcoming?.map((c, i) => <span key={c} className="upcoming-slot">{i === 0 && <span className="upcoming-label">NEXT</span>}<PlayingCard card={c} size="lg" className="upcoming" /></span>)}
            {hand.rabbit?.map(c => <PlayingCard key={c} card={c} size="lg" className="upcoming" />)}
            {(hand.rabbit?.length ?? 0) > 0 && <span className="board-label">RABBIT</span>}
          </div>
        )}
        {hand && twice && (
          <div className="board second" data-testid="board2">
            {twice.map((c, i) => (i < hand.runOutFrom
              ? <PlayingCard key={c} card={c} size="lg" used={highlight2?.used.has(c)} unused={highlight2 !== null && !highlight2.used.has(c)} tilt={highlight2?.tilt.get(c) ?? 0} />
              : <PlayingCard key={c} card={c} size="lg" dealDelayMs={board2Delays[i - hand.runOutFrom] ?? 0} flip used={highlight2?.used.has(c)} unused={highlight2 !== null && !highlight2.used.has(c)} tilt={highlight2?.tilt.get(c) ?? 0} />))}
          </div>
        )}
        {hand?.runItTwice && ritRemainingMs !== null && <div className="rit-wait">Waiting for run it twice decisions ({Math.max(0, Math.ceil(ritRemainingMs / 1000))}s)</div>}
      </div>
    </div>
  );
}
